// Settings / Connect (MUI, /settings). Presentation port of SettingsConnectPage —
// same data/hooks (profile, Jira mapping, MCP URLs, local prefs). Rebuilt with
// mui/* primitives + brainTokens under MuiAppShell.
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useProfile } from "../../lib/profile";
import { getApiUrl } from "../../lib/auth";
import { listJiraUsers, type JiraUserOption } from "../../lib/api";
import { listAdminProfiles, updateAdminProfile, type AdminProfile } from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { SectionCard, StatusPill, Icon } from "../../mui";

const c = brainTokens.color;

function SectionTitle({ icon, children }: { icon: string; children: ReactNode }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "7px", fontSize: 14, fontWeight: 700, color: c.ink }}>
      <Icon name={icon} size={17} stroke={1.8} style={{ color: c.ink3 }} />
      {children}
    </Box>
  );
}

function ScopePillMui({ scope }: { scope: string }) {
  const s = scope === "private" ? "personal" : scope === "shared" ? "company" : scope;
  const icon =
    s === "company" ? "grid" : s.startsWith("dept:") ? "layers" : s.startsWith("personal") ? "users" : undefined;
  const label = s.startsWith("dept:") ? s.slice(5) : s;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        fontSize: 12,
        fontWeight: 600,
        borderRadius: `${brainTokens.radius.pill}px`,
        px: "10px",
        py: "4px",
        bgcolor: s === "company" ? c.tealTint : s.startsWith("dept:") ? c.panelBg2 : c.panelBg,
        color: s === "personal" ? c.muted : c.ink3,
        border: `1px solid ${c.borderLight}`,
      }}
    >
      {icon && <Icon name={icon} size={13} stroke={1.8} />}
      {label}
    </Box>
  );
}

function CopyLine({ label, value }: { label: string; value: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar("Copied to clipboard.", { variant: "success" });
    } catch {
      enqueueSnackbar("Copy failed — select and copy manually.", { variant: "error" });
    }
  };
  return (
    <Box sx={{ mt: "10px" }}>
      <Box sx={{ fontSize: 12, color: c.muted2, mb: "4px" }}>{label}</Box>
      <Box sx={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
        <Box
          component="code"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowX: "auto",
            whiteSpace: "nowrap",
            fontFamily: brainTokens.font.mono,
            bgcolor: c.panelBg2,
            borderRadius: "10px",
            p: "9px 11px",
            fontSize: 12,
            color: c.ink,
          }}
        >
          {value}
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => void copy()}
          aria-label="Copy"
          startIcon={<Icon name="download" size={14} stroke={1.8} />}
          sx={{ flexShrink: 0, color: c.ink3, borderColor: c.border }}
        >
          Copy
        </Button>
      </Box>
    </Box>
  );
}

function JiraMappingCard() {
  const { enqueueSnackbar } = useSnackbar();
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [jiraUsers, setJiraUsers] = useState<JiraUserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [ps, jus] = await Promise.all([listAdminProfiles(), listJiraUsers("")]);
        if (cancelled) return;
        setProfiles(ps.sort((a, b) => (a.displayName ?? a.email ?? a.userId).localeCompare(b.displayName ?? b.email ?? b.userId)));
        setJiraUsers(jus);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load mapping data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setMapping = async (userId: string, jiraAccountId: string) => {
    setSavingId(userId);
    try {
      const updated = await updateAdminProfile(userId, { jiraAccountId });
      setProfiles((prev) => prev.map((p) => (p.userId === userId ? { ...p, jiraAccountId: updated.jiraAccountId } : p)));
      enqueueSnackbar(jiraAccountId ? "Mapped to Jira." : "Unmapped.", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Could not update mapping.", { variant: "error" });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <SectionCard sx={{ mb: "16px" }}>
      <SectionTitle icon="clipboard-list">Jira user mapping</SectionTitle>
      <Typography sx={{ fontSize: 12, color: c.muted2, mt: "4px" }}>
        Map each teammate to their Jira account. Mapped users get a tab on the{" "}
        <Link to="/standup" style={{ color: c.tealInk }}>
          standup board
        </Link>
        . Handles email mismatches (the brain login email need not match the Jira email).
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} sx={{ color: c.teal }} />
        </Box>
      ) : error ? (
        <Typography sx={{ fontSize: 12, color: c.dangerText, mt: "10px" }}>{error}</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: "12px" }}>
          {profiles.map((p) => (
            <Box key={p.userId} sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Box sx={{ fontSize: 12, fontWeight: 600, color: c.ink }}>{p.displayName ?? p.email ?? p.userId}</Box>
                {p.email && <Box sx={{ fontSize: 12, color: c.muted2 }}>{p.email}</Box>}
              </Box>
              {p.jiraAccountId ? (
                <StatusPill tone="success" label="on board" dot />
              ) : (
                <StatusPill tone="neutral" label="unmapped" />
              )}
              <Select
                size="small"
                value={p.jiraAccountId ?? ""}
                disabled={savingId === p.userId}
                onChange={(e) => void setMapping(p.userId, e.target.value)}
                aria-label={`Jira account for ${p.displayName ?? p.userId}`}
                sx={{ minWidth: 200, fontSize: 13 }}
              >
                <MenuItem value="">— Unmapped —</MenuItem>
                {p.jiraAccountId && !jiraUsers.some((j) => j.accountId === p.jiraAccountId) && (
                  <MenuItem value={p.jiraAccountId}>{p.jiraAccountId} (current)</MenuItem>
                )}
                {jiraUsers.map((j) => (
                  <MenuItem key={j.accountId} value={j.accountId}>
                    {j.displayName}
                    {j.emailAddress ? ` · ${j.emailAddress}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ))}
          {profiles.length === 0 && (
            <Typography sx={{ fontSize: 12, color: c.muted2 }}>No users yet.</Typography>
          )}
        </Box>
      )}
    </SectionCard>
  );
}

export function SettingsConnectPageMui() {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const mcpUrl = (() => {
    try {
      return `${getApiUrl()}/mcp`;
    } catch {
      return "https://<brain-domain>/mcp";
    }
  })();
  const [briefTime, setBriefTime] = useState("08:45");
  const [defaultTier, setDefaultTier] = useState("personal");

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ fontSize: 36, fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1 }}>Settings</Box>
        <Box sx={{ fontSize: 14, color: c.muted2, mt: "10px" }}>Connect your AI, your tiers, preferences</Box>
      </Box>

      {profile?.role === "admin" && <JiraMappingCard />}

      <SectionCard sx={{ mb: "16px" }}>
        <SectionTitle icon="plug">Connect your AI</SectionTitle>
        <Typography sx={{ fontSize: 12, color: c.muted2, mt: "4px" }}>
          Add the brain as an MCP server. First use opens a browser for Google sign-in — approve it once.
        </Typography>
        <CopyLine label="Claude Code (CLI)" value={`claude mcp add --transport http aeronet-brain ${mcpUrl}`} />
        <CopyLine label="Claude Desktop / connector URL" value={mcpUrl} />
        <CopyLine
          label="Kiro — mcp.json"
          value={JSON.stringify({ mcpServers: { "aeronet-brain": { transport: "http", url: mcpUrl } } })}
        />
        <Typography sx={{ fontSize: 12, color: c.muted2, mt: "10px" }}>
          Then paste the skill file (
          <Box component="span" sx={{ fontFamily: brainTokens.font.mono }}>
            skills/aeronet-brain/SKILL.md
          </Box>
          ) into your client so it knows when to search and how to scope captures.
        </Typography>
      </SectionCard>

      <SectionCard sx={{ mb: "16px" }}>
        <SectionTitle icon="layers">Your tiers</SectionTitle>
        <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", my: "10px" }}>
          {(profile?.departments ?? []).map((d) => (
            <ScopePillMui key={d} scope={`dept:${d}`} />
          ))}
          <ScopePillMui scope="company" />
          <ScopePillMui scope="personal" />
        </Box>
        <Typography sx={{ fontSize: 12, color: c.muted2 }}>
          Your personal tier is isolated — personal captures and DMs never surface in shared or standup views, and
          exec-review material stays quarantined to you.
        </Typography>
      </SectionCard>

      <SectionCard sx={{ mb: "16px" }}>
        <SectionTitle icon="send">Slack</SectionTitle>
        <Typography sx={{ fontSize: 12, color: c.muted2, mt: "4px" }}>
          Link Slack so{" "}
          <Box component="span" sx={{ fontFamily: brainTokens.font.mono }}>
            /brain tell @you
          </Box>{" "}
          routes to your review queue. Manage the connection in the connector settings.
        </Typography>
        <Box sx={{ mt: "10px" }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/settings-legacy")}
            startIcon={<Icon name="arrow-right" size={14} stroke={1.8} />}
            sx={{ color: c.ink3, borderColor: c.border }}
          >
            Manage connectors
          </Button>
        </Box>
      </SectionCard>

      <SectionCard>
        <SectionTitle icon="settings">Preferences</SectionTitle>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "12px",
            mt: "10px",
          }}
        >
          <Box component="label" sx={{ fontSize: 12, color: c.muted2 }}>
            Morning brief time
            <TextField
              type="time"
              size="small"
              fullWidth
              value={briefTime}
              onChange={(e) => setBriefTime(e.target.value)}
              sx={{ mt: "4px" }}
            />
          </Box>
          <Box component="label" sx={{ fontSize: 12, color: c.muted2 }}>
            Default capture tier
            <Select
              size="small"
              fullWidth
              value={defaultTier}
              onChange={(e) => setDefaultTier(e.target.value)}
              sx={{ mt: "4px", fontSize: 13 }}
            >
              <MenuItem value="personal">personal</MenuItem>
              <MenuItem value="company">company</MenuItem>
              {(profile?.departments ?? []).map((d) => (
                <MenuItem key={d} value={`dept:${d}`}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12, color: c.muted2, mt: "10px" }}>
          Preferences are local for now — server-side persistence is coming.
        </Typography>
      </SectionCard>
    </Box>
  );
}
